import React, { useEffect, useState } from "react";
import { collection, getDocs, updateDoc, doc, deleteDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db } from "../firebaseConfig";
import { getAuth } from "firebase/auth";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import "./global.css"; 

const DataBase = () => {
  const [jobs, setJobs] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userFullName, setUserFullName] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [showNotePopup, setShowNotePopup] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]); // Update to handle multiple files
  const [uploading, setUploading] = useState(false);
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [searchTerm, setSearchTerm] = useState(''); // Search term
  const [sortOption, setSortOption] = useState({ column: '', direction: '' }); // Sort option
  const [filteredJobs, setFilteredJobs] = useState([]); // Filtered jobs for search and sort
  const [companyWorkOrderSearch, setCompanyWorkOrderSearch] = useState(''); // Search by Company Work Order
  const [clientWorkOrderSearch, setClientWorkOrderSearch] = useState(''); // Search by Client Work Order
  const [collections] = useState([
    "EstimateId", "companies", "contractors", "customerCodes", "dailyBills", "fireWatch",
    "generateProposals", "generatedProposals", "jobs", "licenseCertificates", "managementCompanies",
    "maps", "materialList", "moneyIn", "monthlyBills", "monthlyInvoice", "projects", "properties",
    "proposals", "reconcile", "spreadSheet", "staff", "supplies", "tasks", "technicians", "templates",
    "time_logs", "users", "vehicles", "vendors", "warehouseSupplies"
  ]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedDocument, setEditedDocument] = useState({});
  const [newFieldType, setNewFieldType] = useState("string");
  const [newFieldKey, setNewFieldKey] = useState("");
  const [newFieldValue, setNewFieldValue] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (currentUser) {
        setUserId(currentUser.uid);

        const usersRef = collection(db, "users");
        const querySnapshot = await getDocs(usersRef);
        const userDoc = querySnapshot.docs.find((doc) => doc.id === currentUser.uid);

        if (userDoc) {
          setUserRole(userDoc.data().role);
          setUserFullName(`${userDoc.data().firstName} ${userDoc.data().lastName}`);
        }
      }
    };

    const fetchJobs = async () => {
      const jobsRef = collection(db, "jobs");
      const querySnapshot = await getDocs(jobsRef);

      const allJobs = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const noticeServiceTypes = ["Proposal", "Meeting", "Action Items", "Inspection"];
      const hiddenServiceTypes = ["Approved Proposal", "Denied Proposal"];
      const visibleJobs = allJobs.filter(job => job.status === "Active" && !noticeServiceTypes.includes(job.serviceType) && !hiddenServiceTypes.includes(job.serviceType));
      setJobs(visibleJobs);
    };

    fetchUserData();
    fetchJobs();
  }, []);

  useEffect(() => {
    filterAndSortJobs();
  }, [jobs, searchTerm, sortOption, companyWorkOrderSearch, clientWorkOrderSearch]);

  const closeModal = () => {
    setSelectedDocument(null); // Close the document details popup
    setIsEditing(false); // Reset editing state
  };

  const closeNotePopup = () => setShowNotePopup(false);
  const closeVerificationPopup = () => setShowVerificationPopup(false);

  const markJobCompleted = async () => {
    if (selectedJob) {
      const jobDoc = doc(db, "jobs", selectedJob.id);
      await updateDoc(jobDoc, { 
        status: "Completed",
        clientWorkOrder: selectedJob.clientWorkOrder // Save Client Work Order #
      });
      setJobs((prevJobs) => prevJobs.filter((job) => job.id !== selectedJob.id));
      closeModal();
      closeVerificationPopup();
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    setSelectedJob((prev) => ({
      ...prev,
      newFiles: files.map(file => URL.createObjectURL(file))
    }));
  };

  const uploadFiles = async (files) => {
    const storage = getStorage();
    const uploadPromises = files.map(async (file) => {
      const storageRef = ref(storage, `notes/${selectedJob.id}/${file.name}`);
      await uploadBytes(storageRef, file);
      return await getDownloadURL(storageRef);
    });
    return await Promise.all(uploadPromises);
  };

  const saveNote = async () => {
    if (selectedJob && (noteText.trim() !== "" || selectedFiles.length > 0)) {
      setUploading(true);
      let fileURLs = [];

      if (selectedFiles.length > 0) {
        fileURLs = await uploadFiles(selectedFiles);
      }

      const jobDoc = doc(db, "jobs", selectedJob.id);
      const newNote = {
        text: noteText,
        timestamp: new Date().toLocaleString(),
        author: userFullName,
        fileURLs,
        fileNames: selectedFiles.map(file => file.name),
      };

      const updatedNotes = Array.isArray(selectedJob.notes) ? [...selectedJob.notes, newNote] : [newNote];
      await updateDoc(jobDoc, { 
        notes: updatedNotes,
        clientWorkOrder: selectedJob.clientWorkOrder // Ensure clientWorkOrder is saved
      });

      setSelectedJob((prev) => ({ ...prev, notes: updatedNotes }));
      setNoteText("");
      setSelectedFiles([]);
      setUploading(false);
      closeNotePopup();
    }
  };

  const getBackgroundColor = (startDate) => {
    const date = moment(startDate);
    const now = moment();
    const diffDays = now.diff(date, 'days');

    if (diffDays > 30) {
      return 'red';
    } else if (diffDays > 1) {
      return 'orange';
    } else {
      return 'white';
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCompanyWorkOrderSearchChange = (e) => {
    setCompanyWorkOrderSearch(e.target.value);
  };

  const handleClientWorkOrderSearchChange = (e) => {
    setClientWorkOrderSearch(e.target.value);
  };

  const SortButton = ({ columnKey, sortOption, onSort }) => (
    <button onClick={() => onSort(columnKey)}>
      {sortOption.column === columnKey ? (sortOption.direction === 'asc' ? '↑' : '↓') : '↕'}
    </button>
  );

  const sortData = (data, key, direction) => {
    return [...data].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSortChange = (column) => {
    let direction = 'asc';
    if (sortOption.column === column && sortOption.direction === 'asc') {
      direction = 'desc';
    }
    setSortOption({ column, direction });
  };

  const filterAndSortJobs = () => {
    let filtered = jobs.filter(job =>
      job.siteName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.management.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (companyWorkOrderSearch) {
      filtered = filtered.filter(job => job.wo && job.wo.toString().includes(companyWorkOrderSearch));
    }

    if (clientWorkOrderSearch) {
      filtered = filtered.filter(job => job.clientWorkOrder && job.clientWorkOrder.toLowerCase().includes(clientWorkOrderSearch.toLowerCase()));
    }

    setFilteredJobs(filtered);
  };

  const fetchCollectionDocuments = async (collectionName) => {
    try {
      const collectionRef = collection(db, collectionName);
      const querySnapshot = await getDocs(collectionRef);
      const docs = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setDocuments(docs);
      setSelectedCollection(collectionName);
    } catch (error) {
      console.error(`Error fetching documents from ${collectionName}:`, error);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditedDocument((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveChanges = async () => {
    if (!selectedDocument?.id || !selectedCollection) return;

    try {
      const docRef = doc(db, selectedCollection, selectedDocument.id);
      await updateDoc(docRef, editedDocument);
      setDocuments((prevDocs) =>
        prevDocs.map((doc) =>
          doc.id === selectedDocument.id ? { ...editedDocument, id: doc.id } : doc
        )
      );
      setIsEditing(false);
      setSelectedDocument({ ...editedDocument, id: selectedDocument.id });
      alert("Document updated successfully!");
    } catch (error) {
      console.error("Error updating document:", error);
    }
  };

  const handleDeleteDocument = async () => {
    if (!selectedDocument?.id || !selectedCollection) return;

    try {
      const docRef = doc(db, selectedCollection, selectedDocument.id);
      await deleteDoc(docRef);
      setDocuments((prevDocs) =>
        prevDocs.filter((doc) => doc.id !== selectedDocument.id)
      );
      setSelectedDocument(null);
      alert("Document deleted successfully!");
    } catch (error) {
      console.error("Error deleting document:", error);
    }
  };

  const handleAddField = () => {
    let value;
    try {
      switch (newFieldType) {
        case "number":
          value = Number(newFieldValue);
          break;
        case "boolean":
          value = newFieldValue === "true";
          break;
        case "null":
          value = null;
          break;
        case "array":
        case "map":
          value = JSON.parse(newFieldValue);
          break;
        case "timestamp":
          value = new Date(newFieldValue);
          break;
        case "geopoint":
          const [lat, lng] = newFieldValue.split(",").map(Number);
          value = { latitude: lat, longitude: lng };
          break;
        case "reference":
          value = newFieldValue; // Normally use doc() for references
          break;
        default:
          value = newFieldValue;
      }
      setEditedDocument((prev) => ({ ...prev, [newFieldKey]: value }));
      setNewFieldKey("");
      setNewFieldValue("");
      setNewFieldType("string");
    } catch (error) {
      alert("Invalid value for the selected field type.");
    }
  };

  return (
    <div>
      <h1 className="center-text">Data Base</h1>
      <p className="center-text">Role: {userRole}</p>

      <h1 className="center-text">Database Collections</h1>
      <div className="collection-list">
        {collections.map((collectionName) => (
          <button
            key={collectionName}
            className="collection-button"
            onClick={() => fetchCollectionDocuments(collectionName)}
          >
            {collectionName}
          </button>
        ))}
      </div>

      {selectedCollection && (
        <div className="collection-documents">
          <h2>{selectedCollection} Documents</h2>
          {documents.length > 0 ? (
            <ul>
              {documents.map((doc) => {
                // Try to find a meaningful display name from preferred fields
                const displayName =
                  doc.siteName ||
                  doc.companyName ||
                  doc.billName ||
                  doc.management ||
                  doc.property ||
                  doc.propertyName ||
                  doc.association ||
                  doc.firstName ||
                  doc.userName ||
                  doc.model ||
                  doc.itemName ||
                  "";

                return (
                  <li key={doc.id}>
                    <strong>Document ID:</strong> {doc.id}
                    {displayName && <> – <em>{displayName}</em></>}
                    <button
                      className="detail-button"
                      onClick={() => setSelectedDocument(doc)}
                    >
                      View Details
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p>No documents found in this collection.</p>
          )}
        </div>
      )}

      {selectedDocument && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Document Details</h2>
            <p><strong>Document ID:</strong> {selectedDocument.id}</p>
            {isEditing ? (
              <form>
                {Object.keys(selectedDocument).map((key) =>
                  key !== "id" ? (
                    <div key={key}>
                      <label>{key}:</label>
                      <input
                        type="text"
                        name={key}
                        value={editedDocument[key] || ""}
                        onChange={handleEditChange}
                      />
                    </div>
                  ) : null
                )}
                <h3>Add Field</h3>
                <input
                  type="text"
                  placeholder="Field Name"
                  value={newFieldKey}
                  onChange={(e) => setNewFieldKey(e.target.value)}
                />
                <select value={newFieldType} onChange={(e) => setNewFieldType(e.target.value)}>
                  <option value="string">String</option>
                  <option value="number">Number</option>
                  <option value="boolean">Boolean</option>
                  <option value="null">Null</option>
                  <option value="array">Array (JSON)</option>
                  <option value="map">Map (JSON)</option>
                  <option value="timestamp">Timestamp</option>
                  <option value="geopoint">GeoPoint (lat,lng)</option>
                  <option value="reference">Reference (path)</option>
                </select>
                <input
                  type="text"
                  placeholder="Value"
                  value={newFieldValue}
                  onChange={(e) => setNewFieldValue(e.target.value)}
                />
                <button type="button" onClick={handleAddField}>Add Field</button>
                <button
                  className="blue-button"
                  type="button"
                  onClick={handleSaveChanges}
                >
                  Save
                </button>
                <button
                  className="red-button"
                  type="button"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </button>
              </form>
            ) : (
              <>
                {Object.entries(selectedDocument).map(([key, value]) =>
                  key !== "id" ? (
                    <p key={key}>
                      <strong>{key}:</strong> {value}
                    </p>
                  ) : null
                )}
                <button
                  className="blue-button"
                  onClick={() => {
                    setIsEditing(true);
                    setEditedDocument(selectedDocument);
                  }}
                >
                  Edit
                </button>
                <button
                  className="red-button"
                  onClick={handleDeleteDocument}
                >
                  Delete
                </button>
                <button className="gray-button" onClick={closeModal}>
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DataBase;